import { lazy, type ComponentType } from "react";

/**
 * Lazy-load a component with automatic single retry on chunk-load failure.
 * If the retry also fails, performs a one-time hard reload (using a session flag)
 * so a stale chunk reference doesn't blank the page on preview restarts.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const reloadKey = "lovable:chunk-reloaded";
    try {
      return await factory();
    } catch (err) {
      // Try once more before giving up
      try {
        return await factory();
      } catch (err2) {
        const alreadyReloaded =
          typeof window !== "undefined" &&
          window.sessionStorage?.getItem(reloadKey) === "1";

        if (!alreadyReloaded && typeof window !== "undefined") {
          try {
            window.sessionStorage.setItem(reloadKey, "1");
          } catch {
            // ignore
          }
          window.location.reload();
          // Return a never-resolving promise so Suspense keeps the fallback
          return await new Promise<{ default: T }>(() => {});
        }
        throw err2;
      }
    }
  });
}
